import React from "react";
import ButtonGroup from "@material-ui/core/ButtonGroup";
import { getIn, useField } from "formik";

import { ValueSource } from "constants/ruleBooks";

import { useRuleFieldSchema } from "containers/Settings/RuleBooks/Schema/schemaContext";
import { normalisePathParts } from "containers/Settings/RuleBooks/Schema/schema";
import { ledgerEntrySchema } from "containers/Settings/RuleBooks/Schema/ledgerEntrySchema";
import { FieldType } from "containers/Settings/RuleBooks/constants";
import { CountDataSource } from "./CountDataSource";
import { SchemaFieldDataSource } from "./SchemaFieldDataSource";
import { SumDataSource } from "./SumDataSource";
import {
  ConstantDataSource,
  ConstantDataSourceField,
} from "./ConstantDataSource";
import { DataSourcePicker } from "./DataSourcePicker";
import { GlobalBusinessDataSource } from "./GlobalBusinessDataSource";

type DataSourceFieldType = ConstantDataSourceField;

type DataSourceChangedHandler = (dataSource: DataSourceFieldType) => void;

interface DataSourceProps {
  dataTour: string;

  // A list of paths derived from the criteria's `query` property
  criteriaOptions?: string[];

  // Used to limit the schema properties the user can select when using the SchemaFieldDataSource
  // also used to parse entered values for ConstantDataSource
  fieldType: ValueInObj<typeof FieldType>;

  namespace: string;

  onAfterChanged?: DataSourceChangedHandler;

  // Prevents the user from selecting Sum and Count aggregations on Criteria less than the number specified
  // When omitted, the user will be able to select any Criteria aggregation
  allowAggregateFromIndex?: number;

  // Enables the user to select one to one and many to one relationships described in the schema as the datasource
  allowSelectRelations?: boolean;

  // Enables the visibility of one to one and many to one relationships described in the schema as the datasource
  showRelations?: boolean;

  // Enables the user to select 'many-to-many' relationships described in the schema as the datasource
  allowSelectManyRelations?: boolean;

  // Enables the visibility of 'many-to-many' relationships described in the schema as the datasource
  showManyRelations?: boolean;

  // Enables the user to select, and the visibility of properties described in the schema as the datasource
  showProperties?: boolean;

  // The path from the root of the schema, to use as the base for all properties, relationship and many relationships to display
  fieldPath: string;

  // The resolved path of the RuleSet's input field or the input field for the prerequisite rules
  parentInputPath?: string | null;

  // Enables the visibility of the prerequisite rule source
  showPrerequisiteRuleSource?: boolean;

  // Enables the user to select, and the visibility of global businesses as a datasource
  includeGlobalBusinesses?: boolean;

  readOnly?: boolean;
}

export function DataSource(props: DataSourceProps) {
  const {
    dataTour,
    criteriaOptions,
    allowAggregateFromIndex,
    fieldType,
    namespace: ns,
    onAfterChanged,
    allowSelectRelations,
    showRelations,
    allowSelectManyRelations,
    showManyRelations,
    showProperties,
    fieldPath,
    parentInputPath = null,
    showPrerequisiteRuleSource = false,
    includeGlobalBusinesses = false,
    readOnly,
  } = props;

  const { value } = useField(ns)[0];
  const schema = useRuleFieldSchema();

  const valueSource = getIn(value, "source");

  function onAfterDataSourceValueChanged(dataSource) {
    if (typeof onAfterChanged === "function") {
      onAfterChanged(dataSource);
    }
  }

  const showAggregations =
    fieldType === "" || fieldType === undefined || fieldType === "NUMERIC";

  const fullPath = normalisePathParts([parentInputPath, fieldPath]);

  return (
    <ButtonGroup>
      <DataSourcePicker
        dataTour={`${dataTour}DataSource`}
        namespace={ns}
        onAfterChanged={onAfterDataSourceValueChanged}
        showAggregations={showAggregations}
        includeGlobalBusinesses={includeGlobalBusinesses}
        fieldPath={fieldPath}
        parentInputPath={parentInputPath}
        showPrerequisiteRuleSource={showPrerequisiteRuleSource}
        readOnly={readOnly}
      />
      {valueSource === ValueSource.FIELD && (
        <SchemaFieldDataSource
          dataTour={dataTour}
          fieldType={fieldType}
          namespace={ns}
          showRelations={showRelations}
          allowSelectManyRelations={allowSelectManyRelations}
          allowSelectRelations={allowSelectRelations}
          schema={schema}
          showManyRelations={showManyRelations}
          showProperties={showProperties}
          fieldPath={fullPath}
          readOnly={readOnly}
        />
      )}

      {valueSource === ValueSource.RULESET_FIELD && (
        <SchemaFieldDataSource
          dataTour={dataTour}
          fieldType={fieldType}
          namespace={ns}
          showRelations={showRelations}
          allowSelectManyRelations={allowSelectManyRelations}
          allowSelectRelations={allowSelectRelations}
          showManyRelations={showManyRelations}
          showProperties={showProperties}
          schema={schema}
          fieldPath={parentInputPath}
        />
      )}
      {valueSource === ValueSource.DEPENDENCY_VALUE && (
        <SchemaFieldDataSource
          dataTour={dataTour}
          fieldType={fieldType}
          fieldPath=""
          namespace={ns}
          showRelations
          showManyRelations
          showProperties
          schema={ledgerEntrySchema}
        />
      )}
      {valueSource === ValueSource.CONSTANT && (
        <ConstantDataSource
          dataTour={dataTour}
          fieldType={fieldType}
          namespace={ns}
        />
      )}
      {includeGlobalBusinesses &&
        valueSource === ValueSource.GLOBAL_BUSINESS && (
          <GlobalBusinessDataSource dataTour={dataTour} namespace={ns} />
        )}
      {showAggregations && valueSource === ValueSource.SUM && (
        <SumDataSource
          allowFromIndex={allowAggregateFromIndex}
          excludePath={fullPath}
          options={criteriaOptions}
          namespace={ns}
        />
      )}
      {showAggregations && valueSource === ValueSource.COUNT && (
        <CountDataSource
          allowFromIndex={allowAggregateFromIndex}
          excludePath={fullPath}
          options={criteriaOptions}
          namespace={ns}
        />
      )}
    </ButtonGroup>
  );
}
